import React from "react";
import "./styles/globals.css";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { ClerkProvider, useAuth } from "@clerk/clerk-react";

import AuthLayout from "./layouts/Auth";
import AdminLayout from "./layouts/Admin";
import RTLLayout from "./layouts/RTL";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import { SessionExpiredDialog } from "./components/Auth/SessionExpiredDialog";
import { SessionExpiredProvider } from "./contexts/SessionExpiredContext";
import { RouteChangeLoader } from "./components/ui/route-change-loader";
import { RateLimitToastListener } from "./components/RateLimitToastListener";
import { Toaster } from "./components/ui/sonner";
import { AppThemeProvider } from "./components/AppThemeProvider";
import { SignalsProvider } from "./contexts/SignalsContext";
import { upsertNotificationTimezone } from "./services/notificationsApi";
import { deviceIanaTimeZone } from "./utils/reportingTimeZone";
import { toast } from "sonner";
import { isDemoEntryAnalysisEnabled } from "./utils/demoEntryAnalysisFeatureFlag";
import DemoEntryAnalysisApp from "./views/Demo/EntryAnalysis";

// Component to handle root route redirect
function RootRedirect() {
  const { isLoaded, isSignedIn } = useAuth();
  
  React.useEffect(() => {
    if (isLoaded) {
      if (isSignedIn) {
        window.location.assign("/dashboard");
      } else {
        window.location.assign("/auth/signin");
      }
    }
  }, [isLoaded, isSignedIn]);
  
  return null;
}

/** Clear leftover notification toasts when Clerk reports signed-out (e.g. wake on sign-in). */
function DismissToastsOnSignOut() {
  const { isLoaded, isSignedIn } = useAuth();

  React.useEffect(() => {
    if (!isLoaded || isSignedIn) return;
    toast.dismiss();
  }, [isLoaded, isSignedIn]);

  return null;
}

function NotificationTimezoneAutoSync() {
  const { isLoaded, isSignedIn } = useAuth();

  React.useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    const sessionKey = "notifications_timezone_auto_synced";
    if (window.sessionStorage.getItem(sessionKey) === "1") return;

    const timezone = deviceIanaTimeZone();
    upsertNotificationTimezone({
      timezone,
      source: "auto",
      broker_account_id: null,
    })
      .catch((error) => {
        console.error("Failed auto-syncing notification timezone:", error);
      })
      .finally(() => {
        window.sessionStorage.setItem(sessionKey, "1");
      });
  }, [isLoaded, isSignedIn]);

  return null;
}

const publishableKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!publishableKey) {
  throw new Error("Missing Clerk Publishable Key. Please add REACT_APP_CLERK_PUBLISHABLE_KEY to .env.local");
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("Failed to find the root element");
}

const root = createRoot(container);

root.render(
  <ErrorBoundary>
    <ClerkProvider 
      publishableKey={publishableKey} 
      afterSignOutUrl="/auth/signin"
      signInUrl="/auth/signin"
      signUpUrl="/auth/signup"
      afterSignInUrl="/dashboard"
      afterSignUpUrl="/dashboard"
      routerPush={(to) => {
        // Integrate Clerk navigation with BrowserRouter (react-router v5).
        // Browser history updates don't always trigger listeners, so emit a popstate.
        window.history.pushState({}, "", to);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }}
      routerReplace={(to) => {
        window.history.replaceState({}, "", to);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }}
    >
      <SessionExpiredProvider>
        <AppThemeProvider>
        <BrowserRouter>
          <RouteChangeLoader />
          <NotificationTimezoneAutoSync />
          <DismissToastsOnSignOut />
          <SignalsProvider>
            <Switch>
              {/* Auth routes must come first - they're public */}
              <Route path="/auth" component={AuthLayout} />
              {/* RTL routes */}
              <Route path="/rtl" component={RTLLayout} />
              {/* Demo Entry Analysis — public import/prep; results require Clerk inside the page */}
              {isDemoEntryAnalysisEnabled() ? (
                <Route path="/demo/entry-analysis" component={DemoEntryAnalysisApp} />
              ) : null}
              {/* Root redirect */}
              <Route exact path="/">
                <RootRedirect />
              </Route>
              {/* Protected app routes */}
              <ProtectedRoute path="/" component={AdminLayout} />
            </Switch>
          </SignalsProvider>
        </BrowserRouter>
        <Toaster position="top-center" />
        </AppThemeProvider>
        <SessionExpiredDialog />
        <RateLimitToastListener />
      </SessionExpiredProvider>
    </ClerkProvider>
  </ErrorBoundary>
);


